<script lang="ts" setup>
import { segmentation, segmentationV1 } from '@thecompaniesapi/shared/client'

const { getCompanyAttributes } = useApiAttributes()
const { refreshResponseWithApiVersion, version } = useApiDoc()
const { t } = useI18n()
const api = useApi()
const localePath = useLocalePath()

const endpoints: ApiEndpoint[] = [
  {
    method: 'GET',
    path: `/companies/${segmentation.freeQueries.domain}`,
    statuses: [
      {
        code: 200,
        color: 'emerald',
        description: t('statuses.200'),
      },
      {
        code: 404,
        color: 'amber',
        description: t('statuses.404'),
      },
    ],
  },
]

const queryAttributes: ApiAttribute[] = [
  {
    description: t('attributes.domain'),
    example: segmentation.freeQueries.domain,
    name: 'domain',
    required: true,
    type: 'string',
  },
  {
    description: t('attributes.simplified'),
    example: `/companies/${segmentation.freeQueries.domain}?simplified=true`,
    name: 'simplified',
    optional: true,
    type: 'boolean',
  },
  {
    description: t('attributes.refresh'),
    example: `/companies/${segmentation.freeQueries.domain}?refresh=true`,
    name: 'refresh',
    optional: true,
    type: 'boolean',
  },
]

const responseAttributes = computed((): ApiAttribute[] => {
  return [
    {
      attributes: getCompanyAttributes({ version: version.value }),
      description: t('response.company'),
      link: {
        label: t('globals.browseAllDatapoints'),
        to: localePath({
          name: 'api-slug',
          params: { slug: 'datapoints' },
        }),
      },
      name: 'company',
      type: 'object',
    },
  ]
})

async function onVisibilityChange(visibility: boolean) {
  refreshResponseWithApiVersion({
    api,
    execute,
    response,
    visibility,
  })
}

const { data: response, execute, status } = useAsyncData(
  'doc:companies-enrich-from-domain',
  () => {
    const companyDomain = version.value === 'v1'
      ? segmentationV1.freeQueries.domain
      : segmentation.freeQueries.domain

    return api.companies.fetchOne(companyDomain)
  },
  { immediate: false },
)
</script>

<template>
  <ApiDoc
    :endpoints="endpoints"
    :level-to-open="1"
    :loading="status === 'pending'"
    :query-attributes="queryAttributes"
    :response="response?.data"
    :response-attributes="responseAttributes"
    @visibility-change="onVisibilityChange"
  />
</template>

<i18n lang="json">
{
  "en": {
    "attributes": {
      "domain": "The company's domain name (e.g. apple.com, microsoft.com).",
      "refresh": "When set to true, triggers a real-time refresh of the company data including website crawling, social media analysis, and AI-powered enrichment. Note: This operation costs 10 additional credits per request.",
      "simplified": "When set to true, returns a simplified version of the company data. The call will be free and no credits will be deducted."
    },
    "response": {
      "company": "The company profile from our database."
    },
    "statuses": {
      "200": "OK",
      "404": "Not found"
    }
  },
  "es": {
    "attributes": {
      "domain": "El nombre de dominio de la empresa (por ejemplo: apple.com, microsoft.com).",
      "refresh": "Cuando se establece en true, activa una actualización en tiempo real de los datos de la empresa, incluyendo rastreo del sitio web, análisis de redes sociales y enriquecimiento mediante IA. Nota: esta operación cuesta 10 créditos adicionales por solicitud.",
      "simplified": "Cuando se establece en true, devuelve una versión simplificada de los datos de la empresa. La llamada es gratuita y no se descontarán créditos."
    },
    "response": {
      "company": "El perfil de empresa de nuestra base de datos."
    },
    "statuses": {
      "200": "OK",
      "404": "No encontrado"
    }
  },
  "fr": {
    "attributes": {
      "domain": "Le nom de domaine de l'entreprise (par exemple : apple.com, microsoft.com).",
      "refresh": "Lorsqu'il est activé (true), déclenche une mise à jour en temps réel des données de l'entreprise, incluant l'exploration du site web, l'analyse des réseaux sociaux et l'enrichissement via l'IA. Remarque : cette opération coûte 10 crédits supplémentaires par requête.",
      "simplified": "Lorsqu'il est activé (true), renvoie une version simplifiée des données de l'entreprise. L'appel est gratuit et aucun crédit ne sera déduit."
    },
    "response": {
      "company": "Le profil d'entreprise de notre base de données."
    },
    "statuses": {
      "200": "OK",
      "404": "Non trouvée"
    }
  }
}
</i18n>
